import { Link, Stack } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import { layout, radius, space, textStyle } from "@passfirst/ui";

import { Button } from "../src/components/button";
import { ScreenAppbar } from "../src/components/screen-appbar";
import { TabScaffold } from "../src/tab-scaffold";
import { type Colors, useColors } from "../src/theme";
import { trpc } from "../src/trpc";
import { friendlyTrpcError } from "../src/trpc-error";
import { useEffectiveSession } from "../src/use-effective-session";
import { useResponsive } from "../src/use-responsive";
import {
  useSelectedVertical,
  verticalDisplayLabel,
} from "../src/use-selected-vertical";

type TopicMastery = {
  topicId: string;
  topicSlug: string;
  label: string;
  blueprintPct: number;
  theta: number;
  masteryPct: number;
  samples: number;
  recentAccuracy: number | null;
  attemptCount: number;
};

export default function Mastery(): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { isWide } = useResponsive();
  const session = useEffectiveSession();
  const [topics, setTopics] = useState<TopicMastery[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const selected = useSelectedVertical();

  // Sort: weakest topics with at least one attempt come first, then unseen,
  // then strongest. Helps learners spot what to work on. Memoized so we
  // don't re-sort on every render (and kept above the early returns so the
  // hook order stays stable).
  const sorted = useMemo(
    () =>
      [...(topics ?? [])].sort((a, b) => {
        const aSeen = a.samples > 0;
        const bSeen = b.samples > 0;
        if (aSeen && !bSeen) return -1;
        if (!aSeen && bSeen) return 1;
        if (aSeen && bSeen) return a.masteryPct - b.masteryPct;
        return b.blueprintPct - a.blueprintPct;
      }),
    [topics],
  );

  useEffect(() => {
    let cancelled = false;
    if (selected.isLoading) return;
    if (!selected.verticalSlug) {
      if (!cancelled)
        setError("No vertical selected. Pick one from the home page first.");
      return;
    }
    void (async () => {
      try {
        const res = await trpc.mastery.byTopic.query({
          verticalSlug: selected.verticalSlug!,
          // Scope attempt-derived stats to the active jurisdiction so a
          // multi-state vertical doesn't blend stats across states.
          ...(selected.jurisdictionCode
            ? { jurisdictionCode: selected.jurisdictionCode }
            : {}),
        });
        if (!cancelled) setTopics(res);
      } catch (err) {
        // Never render a raw tRPC code/message to the user (#10).
        if (!cancelled) setError(friendlyTrpcError(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected.isLoading, selected.verticalSlug, selected.jurisdictionCode]);

  if (!topics && !error) {
    return (
      <View style={styles.center} accessibilityLabel="Loading mastery">
        <ActivityIndicator />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.center}>
        <Text style={styles.error} accessibilityLiveRegion="polite">
          {error}
        </Text>
        <Link href="/" asChild>
          <Button label="Back home" accessibilityLabel="Back home" fullWidth />
        </Link>
      </View>
    );
  }

  return (
    <TabScaffold
      userLabel={session?.user?.email ?? "Account"}
      metaLabel={verticalDisplayLabel(selected)}
    >
      <Stack.Screen options={{ title: "Mastery" }} />
      <View style={styles.outer}>
        {!isWide ? <ScreenAppbar title="Mastery" /> : null}
        <ScrollView contentContainerStyle={styles.scroll}>
          <Text style={styles.title}>Topic mastery</Text>
        <Text style={styles.subtitle}>
          Updated after every question you answer. Weak topics first.
        </Text>

        <View
          style={styles.list}
          accessibilityRole="list"
          accessibilityLabel="Topics by mastery"
        >
          {sorted.map((t) => {
            const pct = Math.round(t.masteryPct * 100);
            const summary =
              t.samples > 0
                ? `${t.label}, ${pct}% mastery, ${t.samples} ${t.samples === 1 ? "attempt" : "attempts"}`
                : `${t.label}, not started yet`;
            return (
              <View
                key={t.topicId}
                style={styles.row}
                accessibilityRole="text"
                accessibilityLabel={summary}
              >
                <View style={styles.rowHeader}>
                  <Text style={styles.label}>{t.label}</Text>
                  <Text style={styles.pctText}>
                    {t.samples > 0 ? `${pct}%` : "—"}
                  </Text>
                </View>
                <View
                  style={styles.barTrack}
                  accessibilityRole="progressbar"
                  accessibilityValue={{
                    min: 0,
                    max: 100,
                    now: t.samples > 0 ? pct : 0,
                  }}
                >
                  <View
                    style={[
                      styles.barFill,
                      { width: `${pct}%` },
                      t.samples === 0 && styles.barFillUnseen,
                    ]}
                  />
                </View>
                <Text style={styles.meta}>
                  {t.samples > 0
                    ? `${t.samples} ${t.samples === 1 ? "attempt" : "attempts"} · ${
                        t.recentAccuracy != null
                          ? `${Math.round(t.recentAccuracy * 100)}% recent accuracy`
                          : "no recent accuracy"
                      }`
                    : "Not started yet"}
                  {t.blueprintPct > 0
                    ? ` · ${t.blueprintPct.toFixed(0)}% of exam`
                    : ""}
                </Text>
              </View>
            );
          })}
        </View>

        <Link href="/practice" asChild>
          <Button
            label="Practice"
            accessibilityLabel="Start practicing"
            fullWidth
            style={styles.practiceCta}
          />
        </Link>
        </ScrollView>
      </View>
    </TabScaffold>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
  outer: { flex: 1, backgroundColor: colors.background },
  center: {
    flex: 1,
    backgroundColor: colors.background,
    alignItems: "center",
    justifyContent: "center",
    padding: space[6],
    gap: space[3],
  },
  scroll: {
    padding: space[6],
    gap: space[4],
    maxWidth: layout.contentMaxWidth,
    alignSelf: "center",
    width: "100%",
  },
  title: { ...textStyle.h2, color: colors.textPrimary },
  subtitle: { ...textStyle.bodySmall, color: colors.textMuted },
  list: { gap: space[4], marginTop: space[2] },
  row: { gap: space[1] },
  rowHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "baseline",
  },
  label: { ...textStyle.label, fontSize: 15, color: colors.textPrimary, flex: 1 },
  pctText: { ...textStyle.label, color: colors.accentSoftText },
  barTrack: {
    height: 8,
    backgroundColor: colors.surfaceMuted,
    borderRadius: radius.sm,
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    backgroundColor: colors.progressFill,
  },
  barFillUnseen: { backgroundColor: colors.border },
  meta: { ...textStyle.caption, color: colors.textMuted, fontSize: 12 },
  practiceCta: { marginTop: space[2] },
  error: { ...textStyle.bodySmall, color: colors.error, textAlign: "center", marginBottom: space[3] },
  });
}
